import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLanguage } from '../context/LanguageContext';
import ThemeToggle from '../components/ThemeToggle';
import {
  Briefcase,
  FileText,
  Clock,
  DollarSign,
  Shield,
  Calendar,
  CheckCircle,
  Users,
  ArrowRight,
  ChevronRight,
  Scale,
  BarChart3,
  Zap,
  Globe,
  MessageCircle,
  Mail,
  Phone,
  ExternalLink,
  Building2
} from 'lucide-react';

export default function LandingPage() {
  const { t, language, toggleLanguage } = useLanguage();

  // Pricing widget state
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' | 'annual'
  const [userCount, setUserCount] = useState(1);
  const BASE_PRICE = 20; // USD per user / month
  const ANNUAL_DISCOUNT = 0.20;
  const monthlyPerUser = BASE_PRICE;
  const annualPerUser = Math.round(BASE_PRICE * 12 * (1 - ANNUAL_DISCOUNT));
  const effectivePerUserMonth = billingCycle === 'annual'
    ? annualPerUser / 12
    : monthlyPerUser;
  const subtotal = billingCycle === 'annual'
    ? annualPerUser * userCount
    : monthlyPerUser * userCount;
  const youSavePerYear = billingCycle === 'annual'
    ? (monthlyPerUser * 12 - annualPerUser) * userCount
    : 0;
  const fmt = (n) => `$${Math.round(n).toLocaleString()}`;
  const signupHref = `/login?mode=register&cycle=${billingCycle}&users=${userCount}`;

  const features = [
    { icon: FileText, title: t('features.caseManagement'), description: t('features.caseDesc'), color: 'bg-emerald' },
    { icon: Clock, title: t('features.timeTracking'), description: t('features.timeDesc'), color: 'bg-bronze' },
    { icon: DollarSign, title: t('features.billing'), description: t('features.billingDesc'), color: 'bg-navy' },
    { icon: Shield, title: t('features.trust'), description: t('features.trustDesc'), color: 'bg-emerald' },
    { icon: Calendar, title: t('features.calendar'), description: t('features.calendarDesc'), color: 'bg-bronze' },
    { icon: BarChart3, title: t('features.ai'), description: t('features.aiDesc'), color: 'bg-navy' },
    { icon: Users, title: t('features.clientPortal'), description: t('features.clientPortalDesc'), color: 'bg-emerald' }
  ];

  const challenges = [
    { problem: t('challenges.scattered'), solution: t('challenges.scatteredSolution') },
    { problem: t('challenges.lostHours'), solution: t('challenges.lostHoursSolution') },
    { problem: t('challenges.compliance'), solution: t('challenges.complianceSolution') },
    { problem: t('challenges.deadlines'), solution: t('challenges.deadlinesSolution') },
    { problem: t('challenges.communication'), solution: t('challenges.communicationSolution') },
    { problem: t('challenges.manualBilling'), solution: t('challenges.manualBillingSolution') }
  ];

  const stats = [
    { value: '40%', label: t('landing.moreBillableHours') },
    { value: '3x', label: t('landing.fasterRetrieval') },
    { value: '100%', label: t('landing.trustCompliance') },
    { value: '50%', label: t('landing.reducedAdminTime') }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-navy rounded-sm flex items-center justify-center">
                <Scale className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-serif font-bold text-navy">ERP Legal</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {language === 'en' ? 'Features' : 'Características'}
              </a>
              <a href="#challenges" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {language === 'en' ? 'Solutions' : 'Soluciones'}
              </a>
              <a href="#pricing" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {language === 'en' ? 'Pricing' : 'Precios'}
              </a>
            </div>
            <div className="flex items-center gap-4">
              <ThemeToggle />
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                className="gap-2"
                data-testid="language-toggle"
              >
                <Globe className="w-4 h-4" />
                {language === 'en' ? 'ES' : 'EN'}
              </Button>
              <Link to="/login">
                <Button variant="ghost" data-testid="nav-login-btn" className="rounded-none">{t('nav.signIn')}</Button>
              </Link>
              <Link to="/login?mode=register">
                <Button data-testid="nav-get-started-btn" className="btn-primary">{t('nav.getStarted')}</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/30 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            <div className="lg:col-span-7 space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald/10 rounded-full">
                <Zap className="w-4 h-4 text-emerald" />
                <span className="text-sm font-medium text-emerald">{t('landing.badge')}</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-navy dark:text-foreground leading-tight">
                {t('landing.heroTitle')}
                <span className="block text-bronze mt-2">{t('landing.heroSubtitle')}</span>
              </h1>
              <p className="text-lg lg:text-xl text-muted-foreground max-w-xl leading-relaxed">
                {t('landing.heroDescription')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/login?mode=register">
                  <Button data-testid="hero-get-started-btn" className="btn-accent h-14 px-8 text-base">
                    {language === 'es' ? 'Inicia prueba de 14 días' : 'Start 14-day trial'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="https://wa.me/message/5RRTUD6YOXUGF1" target="_blank" rel="noopener noreferrer">
                  <Button className="h-14 px-8 text-base rounded-none bg-[#25D366] hover:bg-[#1DA851] text-white border-0">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    {language === 'es' ? 'Contactar por WhatsApp' : 'Chat on WhatsApp'}
                  </Button>
                </a>
              </div>
              <div className="flex items-center gap-6 pt-4">
                <div className="flex -space-x-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-full bg-secondary border-2 border-white flex items-center justify-center">
                      <Users className="w-4 h-4 text-muted-foreground" />
                    </div>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">
                  <span className="font-semibold text-foreground">100+</span> {t('landing.lawFirmsTrust')}
                </p>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-emerald/20 to-bronze/20 rounded-lg blur-2xl" />
                <div className="relative bg-navy rounded-lg shadow-float overflow-hidden">
                  <div className="p-6 space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/10 rounded flex items-center justify-center">
                        <Scale className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-white font-medium">Smith & Associates</p>
                        <p className="text-white/60 text-sm">Enterprise Plan</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-4">
                      <div className="bg-white/10 rounded p-4">
                        <p className="text-white/60 text-xs uppercase tracking-wider">{language === 'en' ? 'Active Cases' : 'Casos Activos'}</p>
                        <p className="text-2xl font-bold text-white mt-1">147</p>
                      </div>
                      <div className="bg-white/10 rounded p-4">
                        <p className="text-white/60 text-xs uppercase tracking-wider">{language === 'en' ? 'Hours This Week' : 'Horas Esta Semana'}</p>
                        <p className="text-2xl font-bold text-emerald-light mt-1">342.5</p>
                      </div>
                      <div className="bg-white/10 rounded p-4">
                        <p className="text-white/60 text-xs uppercase tracking-wider">{language === 'en' ? 'Revenue MTD' : 'Ingresos del Mes'}</p>
                        <p className="text-2xl font-bold text-bronze-light mt-1">$89.4K</p>
                      </div>
                      <div className="bg-white/10 rounded p-4">
                        <p className="text-white/60 text-xs uppercase tracking-wider">{language === 'en' ? 'Trust Balance' : 'Saldo Fiduciario'}</p>
                        <p className="text-2xl font-bold text-white mt-1">$1.2M</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats / Outcome Band */}
      <section className="py-16 bg-navy">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-2xl lg:text-3xl font-serif font-bold text-white">
              {t('landing.outcomeTitle')}
            </h2>
            <p className="text-white/70 mt-3">{t('landing.outcomeSubtitle')}</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <p className="text-4xl lg:text-5xl font-serif font-bold text-bronze">{stat.value}</p>
                <p className="text-white/70 mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section id="challenges" className="py-20 lg:py-32 bg-paper dark:bg-card">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-serif font-bold text-navy dark:text-foreground">
              {t('landing.challengesSolved').split(',')[0]}, <span className="text-bronze">{t('landing.challengesSolved').split(',')[1]}</span>
            </h2>
            <p className="text-muted-foreground mt-4 text-lg">
              {t('landing.challengesSubtitle')}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {challenges.map((item, idx) => (
              <div key={idx} className="group bg-card text-card-foreground rounded-md border border-border p-6 hover:shadow-float transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded bg-destructive/10 flex items-center justify-center shrink-0">
                    <span className="text-destructive font-bold">✕</span>
                  </div>
                  <p className="text-muted-foreground line-through">{item.problem}</p>
                </div>
                <div className="flex items-start gap-4 mt-4">
                  <div className="w-8 h-8 rounded bg-emerald/10 flex items-center justify-center shrink-0">
                    <CheckCircle className="w-4 h-4 text-emerald" />
                  </div>
                  <p className="text-foreground font-medium">{item.solution}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Comparison */}
      <section className="py-20 lg:py-32 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-serif font-bold text-foreground">
              {t('landing.beforeAfterTitle')}
            </h2>
            <p className="text-muted-foreground mt-4 text-lg">
              {t('landing.beforeAfterSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {/* Before column */}
            <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-6 lg:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-destructive/15 flex items-center justify-center">
                  <span className="text-destructive font-bold text-lg">✕</span>
                </div>
                <h3 className="text-xl font-serif font-semibold text-foreground">
                  {t('landing.beforeLabel')}
                </h3>
              </div>
              <ul className="space-y-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <li key={i} className="flex gap-3">
                    <span className="text-destructive mt-1 shrink-0">•</span>
                    <span className="text-muted-foreground leading-relaxed">
                      {t(`landing.before${i}`)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* After column */}
            <div className="rounded-lg border border-emerald/30 bg-emerald/5 p-6 lg:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-emerald/15 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald" />
                </div>
                <h3 className="text-xl font-serif font-semibold text-foreground">
                  {t('landing.afterLabel')}
                </h3>
              </div>
              <ul className="space-y-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <li key={i} className="flex gap-3">
                    <CheckCircle className="w-4 h-4 text-emerald mt-1 shrink-0" />
                    <span className="text-foreground leading-relaxed">
                      {t(`landing.after${i}`)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 lg:py-32 bg-paper dark:bg-card">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-serif font-bold text-navy dark:text-foreground">
              {t('landing.everythingNeeds')}
            </h2>
            <p className="text-muted-foreground mt-4 text-lg">
              {t('landing.everythingSubtitle')}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className="group relative bg-card text-card-foreground rounded-md border border-border p-8 hover:shadow-float transition-all duration-300 overflow-hidden"
              >
                <div className={`w-12 h-12 ${feature.color} rounded flex items-center justify-center mb-6`}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-serif font-semibold text-navy dark:text-foreground mb-3">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                <ChevronRight className="absolute bottom-8 right-8 w-5 h-5 text-border group-hover:text-bronze transition-colors" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section — one plan + add-ons */}
      <section id="pricing" className="py-20 lg:py-32 bg-secondary/50">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl lg:text-4xl font-serif font-bold text-navy dark:text-foreground">
              {t('landing.simplePricing')}
            </h2>
            <p className="text-muted-foreground mt-4 text-lg">
              {t('landing.pricingSubtitle')}
            </p>
          </div>

          {/* Single unified plan card — interactive */}
          <div className="max-w-2xl mx-auto">
            <div className="bg-navy rounded-md p-8 lg:p-10 relative flex flex-col ring-2 ring-bronze shadow-float">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-bronze text-white text-xs font-bold uppercase tracking-wider rounded-full">
                {t('pricing.planName')}
              </div>
              <p className="text-sm text-white/70">{t('pricing.planTagline')}</p>

              {/* Billing cycle toggle */}
              <div className="mt-5 inline-flex self-start rounded-md border border-white/20 p-1 bg-white/5">
                <button
                  type="button"
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-4 py-1.5 text-sm rounded transition-all ${
                    billingCycle === 'monthly'
                      ? 'bg-bronze text-white font-medium'
                      : 'text-white/70 hover:text-white'
                  }`}
                  data-testid="cycle-monthly"
                >
                  {t('pricing.monthly')}
                </button>
                <button
                  type="button"
                  onClick={() => setBillingCycle('annual')}
                  className={`px-4 py-1.5 text-sm rounded transition-all flex items-center gap-2 ${
                    billingCycle === 'annual'
                      ? 'bg-bronze text-white font-medium'
                      : 'text-white/70 hover:text-white'
                  }`}
                  data-testid="cycle-annual"
                >
                  {t('pricing.annual')}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                    billingCycle === 'annual' ? 'bg-white text-bronze' : 'bg-emerald text-white'
                  }`}>
                    {t('pricing.annualBadge')}
                  </span>
                </button>
              </div>

              {/* Price per user */}
              <p className="mt-6">
                <span className="text-5xl font-serif font-bold text-white">
                  ${Math.round(effectivePerUserMonth)}
                </span>
                <span className="text-white/70 ml-1">
                  {billingCycle === 'annual' ? `${t('pricing.pricePerUserPerMonth')}*` : t('pricing.pricePerUserPerMonth')}
                </span>
              </p>
              {billingCycle === 'annual' && (
                <p className="text-xs text-white/60 mt-1">
                  * {t('pricing.basePerUserAnnual')}
                </p>
              )}

              {/* User count */}
              <div className="mt-5 flex items-end gap-4">
                <div className="flex-1">
                  <label className="text-xs text-white/70 block mb-1.5">
                    {t('pricing.usersLabel')}
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setUserCount((n) => Math.max(1, n - 1))}
                      className="w-9 h-9 rounded bg-white/10 hover:bg-white/20 text-white text-lg leading-none"
                      aria-label="decrement"
                    >−</button>
                    <Input
                      type="number"
                      min="1"
                      max="9999"
                      value={userCount}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        setUserCount(Number.isFinite(v) && v > 0 ? v : 1);
                      }}
                      className="w-20 h-9 text-center bg-white/10 border-white/20 text-white rounded"
                      data-testid="user-count-input"
                    />
                    <button
                      type="button"
                      onClick={() => setUserCount((n) => Math.min(9999, n + 1))}
                      className="w-9 h-9 rounded bg-white/10 hover:bg-white/20 text-white text-lg leading-none"
                      aria-label="increment"
                    >+</button>
                  </div>
                  <p className="text-[11px] text-white/50 mt-1">{t('pricing.usersHint')}</p>
                </div>
              </div>

              {/* Subtotal */}
              <div className="mt-5 rounded-md bg-white/10 p-4">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-white/80">
                    {billingCycle === 'annual' ? t('pricing.subtotalAnnual') : t('pricing.subtotalMonthly')}
                  </span>
                  <div className="text-right">
                    <span className="text-2xl font-serif font-bold text-white">{fmt(subtotal)}</span>
                    <span className="text-xs text-white/60 ml-1">
                      {billingCycle === 'annual' ? t('pricing.billedAnnually') : t('pricing.billedMonthly')}
                    </span>
                  </div>
                </div>
                {youSavePerYear > 0 && (
                  <p className="text-xs text-emerald mt-2 font-medium">
                    {t('pricing.youSave')} {fmt(youSavePerYear)}/{language === 'es' ? 'año' : 'year'}
                  </p>
                )}
              </div>

              <p className="text-white/90 text-sm mt-6 font-medium uppercase tracking-wider">
                {t('pricing.includedTitle')}
              </p>
              <ul className="mt-3 grid sm:grid-cols-2 gap-y-2 gap-x-4">
                {t('pricing.included').map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-white/90">
                    <CheckCircle className="w-4 h-4 text-bronze shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link to={signupHref} className="block mt-8">
                <Button className="w-full h-12 text-base font-semibold bg-emerald hover:bg-emerald/90 text-white" data-testid="start-trial-btn">
                  {language === 'es' ? 'Inicia prueba de 14 días' : 'Start 14-day trial'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <p className="text-xs text-white/60 mt-3 text-center">
                {t('pricing.cardRequiredNote')}
              </p>
            </div>
          </div>

          {/* Add-ons */}
          <div className="mt-16 text-center max-w-3xl mx-auto">
            <h3 className="text-2xl lg:text-3xl font-serif font-bold text-navy dark:text-foreground">
              {t('pricing.addonsTitle')}
            </h3>
            <p className="text-muted-foreground mt-3">
              {t('pricing.addonsSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mt-10 max-w-5xl mx-auto">
            {/* AI add-ons */}
            <div className="bg-card text-card-foreground rounded-md border border-border p-6">
              <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
                {t('pricing.aiAddonsTitle')}
              </p>
              <ul className="space-y-3">
                {[
                  { q: 300, p: 15 },
                  { q: 600, p: 30 },
                  { q: 1200, p: 60 },
                  { q: 2400, p: 120 },
                ].map((pack) => (
                  <li key={pack.q} className="flex items-center justify-between border-b border-border pb-3 last:border-0">
                    <span className="text-sm">{t('pricing.aiAddonLabel').replace('{n}', pack.q.toLocaleString())}</span>
                    <span className="text-base font-semibold text-navy dark:text-foreground">${pack.p}{t('pricing.perMonth')}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Storage add-ons */}
            <div className="bg-card text-card-foreground rounded-md border border-border p-6">
              <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
                {t('pricing.storageAddonsTitle')}
              </p>
              <ul className="space-y-3">
                {[
                  { gb: 10, p: 10 },
                  { gb: 20, p: 15 },
                  { gb: 40, p: 30 },
                  { gb: 80, p: 60 },
                ].map((pack) => (
                  <li key={pack.gb} className="flex items-center justify-between border-b border-border pb-3 last:border-0">
                    <span className="text-sm">{t('pricing.storageAddonLabel').replace('{n}', pack.gb)}</span>
                    <span className="text-base font-semibold text-navy dark:text-foreground">${pack.p}{t('pricing.perMonth')}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-navy relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald to-bronze" />
        </div>
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative">
          <h2 className="text-3xl lg:text-4xl font-serif font-bold text-white">
            {t('landing.readyTransform')}
          </h2>
          <p className="text-white/70 mt-4 text-lg max-w-2xl mx-auto">
            {t('landing.ctaDescription')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            <Link to="/login?mode=register">
              <Button data-testid="cta-get-started-btn" className="btn-accent h-14 px-10 text-base">
                {language === 'es' ? 'Inicia prueba de 14 días' : 'Start 14-day trial'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="https://wa.me/message/5RRTUD6YOXUGF1" target="_blank" rel="noopener noreferrer">
              <Button className="h-14 px-10 text-base rounded-none bg-[#25D366] hover:bg-[#1DA851] text-white border-0">
                <MessageCircle className="w-5 h-5 mr-2" />
                {language === 'es' ? 'Contactar por WhatsApp' : 'Chat on WhatsApp'}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="py-16 bg-navy-dark border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            {/* ERP Legal */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-white/10 rounded-sm flex items-center justify-center">
                  <Scale className="w-4 h-4 text-white" />
                </div>
                <span className="text-lg font-serif font-bold text-white">ERP Legal</span>
              </div>
              <p className="text-sm text-white/60 leading-relaxed">
                {language === 'es'
                  ? 'El único software legal dominicano con facturación DOP/USD, cuentas fiduciarias IOLTA y asistente de investigación jurídica integrado. Un solo lugar para todo su bufete.'
                  : 'The only Dominican legal software with DOP/USD billing, IOLTA trust accounts, and a built-in legal research assistant. One place for your entire firm.'}
              </p>
              <div className="flex items-center gap-4 mt-4">
                <Link to="/privacy" className="text-sm text-white/50 hover:text-white transition-colors">{t('footer.privacy')}</Link>
                <Link to="/privacy" className="text-sm text-white/50 hover:text-white transition-colors">{t('footer.terms')}</Link>
              </div>
            </div>

            {/* Quiénes somos — MSSPRD */}
            <div>
              <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-bronze" />
                {language === 'es' ? 'Quiénes somos' : 'About us'}
              </h4>
              <p className="text-sm text-white/60 leading-relaxed">
                {language === 'es'
                  ? 'MSSPRD protege empresas contra ciberataques con monitoreo 24/7, detección de vulnerabilidades, respuesta a incidentes, seguridad en la nube y protección de equipos. Somos su equipo de ciberseguridad — sin tener que contratar uno.'
                  : 'MSSPRD protects businesses from cyberattacks with 24/7 monitoring, vulnerability detection, incident response, cloud security, and endpoint protection. Think of us as your cybersecurity team — without having to hire one.'}
              </p>
            </div>

            {/* Contacto */}
            <div>
              <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
                {language === 'es' ? 'Contacto' : 'Contact'}
              </h4>
              <ul className="space-y-3 text-sm text-white/60">
                <li className="flex items-start gap-2">
                  <Building2 className="w-4 h-4 text-bronze shrink-0 mt-0.5" />
                  <span>MSSPRD — {language === 'es'
                    ? 'Tecnología, Ciberseguridad y Soporte Gestionado'
                    : 'Technology, Cybersecurity & Managed Support'}</span>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-bronze shrink-0" />
                  <a href="mailto:info@mssprd.com" className="hover:text-white transition-colors">info@mssprd.com</a>
                </li>
                <li className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-bronze shrink-0" />
                  <a href="https://www.mssprd.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors flex items-center gap-1">
                    www.mssprd.com <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-bronze shrink-0" />
                  <a href="tel:+18498780923" className="hover:text-white transition-colors">+1 849 878 0923</a>
                </li>
                <li className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-[#25D366] shrink-0" />
                  <a href="https://wa.me/message/5RRTUD6YOXUGF1" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                    WhatsApp
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6 text-center">
            <p className="text-sm text-white/40">
              {t('footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
